// Tenký fetch wrapper. Same-origin (přes vite proxy v devu, přes nginx v produkci).
// Tělo chyby z backendu má tvar { error, message, details? }.
import { outbox, isQueueable } from './offline';

const SERVER_RESTART = 'Server se právě aktualizuje. Zkus to za chvíli, rozepsané zůstává.';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

async function handle(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const isJson = res.headers.get('content-type')?.includes('application/json');
  // Server se restartuje (nasazení): odpověď nepřišla od aplikace, zápis se opakuje bezpečně.
  if (!isJson && res.status >= 502 && res.status <= 504) throw new ApiError(0, SERVER_RESTART);
  const body = isJson ? await res.json() : await res.text();
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'message' in body
        ? String((body as Record<string, unknown>).message)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, (body as Record<string, unknown>)?.details);
  }
  return body;
}

const jsonHeaders = { 'content-type': 'application/json' };

const NO_CONNECTION = 'Spojení se přerušilo. Zkus to znovu, dvakrát se nic nezapíše.';

// Mrtvá Wi-Fi nikdy neodpoví. Bez limitu by se spinner točil donekonečna.
const TIMEOUT_MS = 30_000;
const SLOW_MS = 180_000;
const SLOW_PATH = /^\/api\/(ai|backups)/;

async function request(path: string, init: RequestInit) {
  try {
    // Starší iPhony AbortSignal.timeout neznají. Bez limitu je to pořád lepší než nefunkční aplikace.
    const signal = AbortSignal.timeout?.(SLOW_PATH.test(path) ? SLOW_MS : TIMEOUT_MS);
    return await handle(await fetch(path, { credentials: 'same-origin', signal, ...init }));
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, NO_CONNECTION);
  }
}

// Když odpověď nedorazí, klient neví, jestli se zápis provedl. Stejné opakování proto nese
// stejný klíč a server ho provede jen jednou. Po doručené odpovědi je další odeslání nový zápis.
// Přihlášení se neopakuje z paměti: nastavuje cookie a tělo požadavku nese heslo.
const retryKeys = new Map<string, string>();
const newKey = () => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

async function postOnce(path: string, data: unknown) {
  const body = JSON.stringify(data);
  if (path.startsWith('/api/auth/')) return request(path, { method: 'POST', headers: jsonHeaders, body });
  const attempt = `${path}|${body}`;
  const key = retryKeys.get(attempt) ?? newKey();
  try {
    const result = await request(path, { method: 'POST', headers: { ...jsonHeaders, 'idempotency-key': key }, body });
    retryKeys.delete(attempt);
    return result;
  } catch (err) {
    if (err instanceof ApiError && err.status === 0) retryKeys.set(attempt, key);
    else retryKeys.delete(attempt);
    throw err;
  }
}

export const api = {
  get: <T>(path: string) => request(path, {}) as Promise<T>,
  post: <T>(path: string, data: unknown) => {
    // Offline: vybrané akce (přechod fáze, poznámka) ulož do fronty a přehraj po připojení.
    if (typeof navigator !== 'undefined' && !navigator.onLine && isQueueable(path)) {
      outbox.enqueue('POST', path, data);
      return Promise.resolve({ queued: true } as unknown as T);
    }
    return postOnce(path, data) as Promise<T>;
  },
  patch: <T>(path: string, data: unknown) =>
    request(path, { method: 'PATCH', headers: jsonHeaders, body: JSON.stringify(data) }) as Promise<T>,
  put: <T>(path: string, data: unknown) =>
    request(path, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(data) }) as Promise<T>,
  del: (path: string) => request(path, { method: 'DELETE' }) as Promise<void>,
};
